/**
 * Hero content for /search and /collections/[handle].
 *
 * The hero used to resolve inline in search/page.tsx and only understood two
 * things: an active collection, or exactly one selected category. Everything
 * else fell through to "Shop all products" with the generic cover, so filtering
 * to a brand, a strain type, or the sale view left the banner claiming you were
 * browsing the whole catalog.
 *
 * The category→image map also keyed off values the catalog does not actually
 * use. It knew "Vaporizer"/"Vaporizers" but the real classificationName is
 * "Vapes" (1,766 products), and it had no entry for "Concentrates" (1,385),
 * "Beverages", "Oil", "Capsules", "Batteries & Devices", "Glassware",
 * "Rolling Papers", "Storage & Care" or "Pipes". All of them silently rendered
 * the fallback image.
 *
 * Pulled out here so the precedence is readable and testable rather than being
 * a chain of ternaries inside a 1,500-line component.
 */

/**
 * Cover images that exist under public/media/categories/covers/.
 *
 * All photography. The first nine are the original shots; the rest were added
 * because those categories either had no artwork and fell back to "all", or
 * borrowed a cover that showed the wrong product (Beverages used the edibles
 * photo, Pipes used accessories).
 */
type CoverKey =
  | "all"
  | "flowers"
  | "pre-rolls"
  | "extracts"
  | "edibles"
  | "vape"
  | "topicals"
  | "accessories"
  | "merch"
  | "beverages"
  | "capsules"
  | "oils"
  | "glassware"
  | "pipes"
  | "rolling-papers"
  | "storage"
  | "seeds"
  | "ashtray"
  | "batteries"
  | "candles"
  | "grinder"
  | "lighters"
  | "rolling-essentials"
  | "siliconware"
  | "gift-card";

/**
 * Real `Product.classificationName` values → cover image.
 * Keys are matched case-insensitively; anything unknown falls back to "all".
 */
const CATEGORY_COVER: Record<string, CoverKey> = {
  // Core cannabis categories
  flower: "flowers",
  flowers: "flowers",
  "pre-roll": "pre-rolls",
  "pre-rolls": "pre-rolls",
  vapes: "vape",
  vaporizer: "vape",
  vaporizers: "vape",
  "batteries & devices": "vape",
  concentrates: "extracts",
  extract: "extracts",
  extracts: "extracts",
  edibles: "edibles",
  edible: "edibles",
  beverages: "beverages",
  drinks: "beverages",
  oil: "oils",
  oils: "oils",
  capsules: "capsules",
  tincture: "oils",
  tinctures: "oils",
  topical: "topicals",
  topicals: "topicals",
  // Non-consumables
  accessories: "accessories",
  glassware: "glassware",
  pipes: "pipes",
  "rolling papers": "rolling-papers",
  "rolling essentials": "rolling-essentials",
  "storage & care": "storage",
  seeds: "seeds",
  ashtray: "ashtray",
  ashtrays: "ashtray",
  batteries: "batteries",
  candles: "candles",
  grinder: "grinder",
  grinders: "grinder",
  lighters: "lighters",
  siliconware: "siliconware",
  "integrated gift card": "gift-card",
  "gift card": "gift-card",
  clothing: "merch",
  merch: "merch",
};

/** Shopper-facing label for a raw category value. */
const CATEGORY_LABEL: Record<string, string> = {
  flower: "Flower",
  "pre-roll": "Pre-Rolls",
  vapes: "Vapes",
  concentrates: "Concentrates",
  edibles: "Edibles",
  beverages: "Beverages",
  oil: "Oils",
  capsules: "Capsules",
  topicals: "Topicals",
  glassware: "Glassware",
  "batteries & devices": "Batteries & Devices",
  "rolling papers": "Rolling Papers",
  "storage & care": "Storage & Care",
  pipes: "Pipes",
  clothing: "Merch",
  accessories: "Accessories",
  seeds: "Seeds",
};

/**
 * Per-category blurb. Only "Flowers" had one before, so every other category
 * showed the same generic sentence as the unfiltered catalog.
 */
const CATEGORY_DESCRIPTION: Record<string, string> = {
  flower:
    "Browse cannabis flower by strain type and potency. Compare terpenes, THC and CBD, then add your favourites to cart for pickup or delivery.",
  "pre-roll":
    "Ready-to-go pre-rolls in singles and multi-packs. Filter by strain type, potency and pack size to find the right one.",
  vapes:
    "510 cartridges, disposables and pods. Filter by strain type, potency and format to match your device.",
  concentrates:
    "Shatter, rosin, resin, hash and more. Filter by extraction process and potency to find the texture and strength you want.",
  edibles:
    "Gummies, chocolates, baked goods and more. Filter by THC and CBD per piece so you can dose the way you like.",
  beverages:
    "Cannabis drinks and seltzers. Filter by THC and CBD per can to find something easy to measure.",
  oil: "Ingestible oils and tinctures. Filter by THC and CBD to dose precisely, drop by drop.",
  capsules:
    "Pre-measured cannabis capsules. Filter by THC and CBD per capsule for consistent, repeatable dosing.",
  topicals:
    "Creams, balms and roll-ons applied to the skin. Browse by format and cannabinoid content.",
  glassware: "Bongs, rigs, bowls and glass accessories to pair with your flower or concentrates.",
  "batteries & devices":
    "Batteries, chargers and vaporizer hardware. Find the right fit for your cartridges.",
  "rolling papers": "Papers, cones, tips and rolling supplies.",
  "storage & care": "Storage, cleaning and care products to keep your gear and flower in good shape.",
  pipes: "Hand pipes, one-hitters and portable smoking gear.",
  clothing: "Dank apparel and merch.",
  accessories: "Grinders, storage, papers and everything else that goes with it.",
  seeds: "Cannabis seeds for home cultivation.",
};

const GENERIC_DESCRIPTION =
  "Browse our selection by type and potency. Use filters to compare THC/CBD and price, then add your favourites to cart for pickup or delivery.";

const STRAIN_TYPE_LABEL: Record<string, string> = {
  indica: "Indica",
  sativa: "Sativa",
  hybrid: "Hybrid",
};

export type SearchHeroInput = {
  /** Resolved collection when one is active; takes precedence over everything. */
  collection?: {
    title?: string | null;
    description?: string | null;
    bannerImageUrl?: string | null;
  } | null;
  categories?: readonly string[];
  brands?: readonly string[];
  strainType?: string;
  onSale?: boolean;
};

export type SearchHero = {
  title: string;
  description: string;
  /** Empty string means "no second crumb", i.e. plain Home > Shop. */
  breadcrumbLabel: string;
  coverImageSrc: string;
};

const norm = (value: string | null | undefined) => (value || "").trim().toLowerCase();

export function categoryCoverKey(category: string | null | undefined): CoverKey {
  return CATEGORY_COVER[norm(category)] || "all";
}

export function categoryLabel(category: string | null | undefined): string {
  const key = norm(category);
  if (!key) return "";
  return CATEGORY_LABEL[key] || (category || "").trim();
}

/**
 * Two artwork sets, because the two places a category picture appears want
 * opposite shapes:
 *
 *   cover-<key>.webp   wide banner strip (~4:1), light cut-out style, sits
 *                      behind the heading in the hero band
 *   hero-<key>.webp    16:9 studio photograph, fills a category card
 *
 * Dropping the strips and using the photographs everywhere left the hero band
 * cropping a 16:9 frame down to a ~4:1 slot, which cut the subject off top and
 * bottom. Only nine strips were ever drawn, so a category without one falls back
 * to the generic strip, which is how the band behaved before.
 */
const CATEGORIES_WITH_STRIP = new Set<CoverKey>([
  'all',
  'flowers',
  'pre-rolls',
  'extracts',
  'edibles',
  'vape',
  'topicals',
  'accessories',
  'merch',
]);

/** Hero-band artwork: the wide strip. */
function coverSrc(key: CoverKey): string {
  const strip = CATEGORIES_WITH_STRIP.has(key) ? key : 'all';
  return `/media/categories/covers/cover-${strip}.webp`;
}

/** Card artwork: the studio photograph. Every category has one. */
export function categoryImageSrc(key: CoverKey): string {
  return `/media/categories/covers/hero-${key}.webp`;
}

/** Join labels the way a person would: "A", "A & B", "A, B & C". */
function humanJoin(values: string[]): string {
  if (values.length <= 1) return values[0] || "";
  if (values.length === 2) return `${values[0]} & ${values[1]}`;
  return `${values.slice(0, -1).join(", ")} & ${values[values.length - 1]}`;
}

/**
 * Resolve hero title, description, breadcrumb and cover image.
 *
 * Precedence, most specific first:
 *   1. active collection  (its own title/description/banner)
 *   2. category selection (single or several)
 *   3. brand selection
 *   4. strain type
 *   5. on-sale view
 *   6. whole catalog
 *
 * Brand and strain type refine a category rather than replacing it, so
 * "Flower" + "Indica" reads "Indica Flower" and keeps the flower cover.
 */
export function resolveSearchHero(input: SearchHeroInput): SearchHero {
  const categories = (input.categories || []).filter(Boolean);
  const brands = (input.brands || []).filter(Boolean);
  const strainLabel = STRAIN_TYPE_LABEL[norm(input.strainType)] || "";
  const onSale = Boolean(input.onSale);

  // 1. Collection wins outright — it is editorial content with its own artwork.
  if (input.collection) {
    const title = (input.collection.title || "").trim() || "Collection";
    return {
      title,
      description:
        (input.collection.description || "").trim() ||
        "Browse products in this collection and use filters to refine by potency, price and format.",
      breadcrumbLabel: title,
      coverImageSrc:
        (input.collection.bannerImageUrl || "").trim() || coverSrc("all"),
    };
  }

  const categoryLabels = categories.map(categoryLabel).filter(Boolean);
  // With several categories the cover follows the first: mixing artwork is
  // worse than picking one, and the heading already names all of them.
  const cover = categories.length > 0 ? categoryCoverKey(categories[0]) : "all";

  let title: string;
  let description: string;
  let breadcrumbLabel = "";

  if (categoryLabels.length > 0) {
    const joined = humanJoin(categoryLabels);
    breadcrumbLabel = joined;
    // A strain type qualifies the category; a brand does too, but only when
    // exactly one is picked, otherwise the heading turns into a list.
    const qualifier = strainLabel || (brands.length === 1 ? brands[0] : "");
    title = qualifier ? `${qualifier} ${joined}` : `Cannabis ${joined}`;
    description =
      (categories.length === 1 ? CATEGORY_DESCRIPTION[norm(categories[0])] : "") ||
      GENERIC_DESCRIPTION;
  } else if (brands.length === 1) {
    breadcrumbLabel = brands[0];
    title = brands[0];
    description = `Everything we carry from ${brands[0]}. Filter by category, potency and price to narrow it down.`;
  } else if (brands.length > 1) {
    breadcrumbLabel = humanJoin([...brands]);
    title = humanJoin([...brands]);
    description = GENERIC_DESCRIPTION;
  } else if (strainLabel) {
    breadcrumbLabel = strainLabel;
    title = `${strainLabel} cannabis`;
    description = `Browse every ${strainLabel.toLowerCase()} product we carry, across flower, pre-rolls, vapes and edibles.`;
  } else if (onSale) {
    breadcrumbLabel = "On sale";
    title = "On sale now";
    description =
      "Everything currently discounted. Prices update as promotions start and end, so stock moves quickly.";
  } else {
    title = "Shop all products";
    description = GENERIC_DESCRIPTION;
  }

  // The sale view is a lens over whatever else is selected, so mark it in the
  // heading rather than discarding the category or brand the shopper chose.
  if (onSale && title !== "On sale now") {
    title = `${title} on sale`;
  }

  return { title, description, breadcrumbLabel, coverImageSrc: coverSrc(cover) };
}
